import {
  ActionIcon,
  Box,
  Group,
  Indicator,
  Loader,
  Menu,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import type { ComponentType } from "react";
import {
  IconBell,
  IconCircleCheck,
  IconClipboardList,
  IconCoin,
  IconCreditCard,
  IconFileText,
  IconInbox,
  IconLockOpen,
  IconMessage,
  IconShieldCheck,
  IconShieldX,
  IconSparkles,
  IconUserCheck,
  IconUserX,
  IconUsers,
} from "@tabler/icons-react";
import type { UserNotificationType } from "@shared/notifications";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";
import classes from "./NotificationBell.module.css";

const MENU_WIDTH = 380;
/** Mantine ActionIcon size="lg" — used to align panel right edge with bell left edge */
const BELL_TARGET_WIDTH = 42;

/** Approximate row height × 4 visible rows before scroll. */
const VISIBLE_NOTIFICATION_ROWS = 4;
const NOTIFICATION_ROW_APPROX_PX = 88;
const NOTIFICATION_LIST_SCROLL_HEIGHT = VISIBLE_NOTIFICATION_ROWS * NOTIFICATION_ROW_APPROX_PX;

const NAVY_BORDER = "#04305c";
const UNREAD_BG = "var(--mantine-color-blue-0)";
const READ_BG = "#ffffff";
const BORDER_COLOR = "var(--mantine-color-gray-2)";
const UNREAD_DOT = "var(--mantine-color-blue-5)";

type NotificationIconProps = { size?: number; stroke?: number; color?: string };

type NotificationIconConfig = {
  icon: ComponentType<NotificationIconProps>;
  iconColor: string;
  circleBg: string;
};

function getNotificationIconConfig(type: UserNotificationType): NotificationIconConfig {
  switch (type) {
    case "CHAT_MESSAGE":
      return {
        icon: IconMessage,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "DOCUMENT_SHARED":
      return {
        icon: IconFileText,
        iconColor: "var(--mantine-color-green-6)",
        circleBg: "var(--mantine-color-green-0)",
      };
    case "VERIFICATION_APPROVED":
      return {
        icon: IconShieldCheck,
        iconColor: "var(--mantine-color-teal-6)",
        circleBg: "var(--mantine-color-teal-0)",
      };
    case "VERIFICATION_REJECTED":
      return {
        icon: IconShieldX,
        iconColor: "var(--mantine-color-orange-6)",
        circleBg: "var(--mantine-color-orange-0)",
      };
    case "REQUEST_RECEIVED":
    case "REQUEST_ACCEPTED":
    case "REQUEST_DECLINED":
      return {
        icon: IconInbox,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "PARTNER_PAYMENT":
    case "SENDER_PAYMENT_REQUIRED":
    case "SENDER_PAYMENT_FINAL_REMINDER":
      return {
        icon: IconCreditCard,
        iconColor: "var(--mantine-color-violet-6)",
        circleBg: "var(--mantine-color-violet-0)",
      };
    case "CREDITS_RESTORED_TIMEOUT":
      return {
        icon: IconCoin,
        iconColor: "var(--mantine-color-yellow-7)",
        circleBg: "var(--mantine-color-yellow-0)",
      };
    case "WORKSPACE_UNLOCKED":
    case "DISCOVERY_UNLOCKED":
      return {
        icon: IconLockOpen,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "WELCOME_FIRST_LOGIN":
    case "WELCOME_RETURNING":
      return {
        icon: IconSparkles,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "FACILITATOR_ASSIGNMENT_ACCEPTED":
      return {
        icon: IconUserCheck,
        iconColor: "var(--mantine-color-teal-6)",
        circleBg: "var(--mantine-color-teal-0)",
      };
    case "FACILITATOR_ASSIGNMENT_REJECTED":
      return {
        icon: IconUserX,
        iconColor: "var(--mantine-color-orange-6)",
        circleBg: "var(--mantine-color-orange-0)",
      };
    case "FACILITATOR_ASSIGNMENT_REQUEST_RECEIVED":
    case "FACILITATOR_PARTNER_RECOMMENDED":
    case "ECOSYSTEM_HEAD_JOIN_REQUEST":
    case "ECOSYSTEM_HEAD_PARTNER_RECOMMENDED":
      return {
        icon: IconInbox,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "ECOSYSTEM_HEAD_JOIN_ACCEPTED":
    case "ECOSYSTEM_HEAD_VERIFICATION_APPROVED":
      return {
        icon: IconShieldCheck,
        iconColor: "var(--mantine-color-teal-6)",
        circleBg: "var(--mantine-color-teal-0)",
      };
    case "ECOSYSTEM_HEAD_JOIN_REJECTED":
    case "ECOSYSTEM_HEAD_VERIFICATION_REJECTED":
      return {
        icon: IconShieldX,
        iconColor: "var(--mantine-color-orange-6)",
        circleBg: "var(--mantine-color-orange-0)",
      };
    case "ECOSYSTEM_HEAD_QUESTIONNAIRE_SUBMITTED":
    case "ECOSYSTEM_HEAD_MORE_INFO_SUBMITTED":
    case "ECOSYSTEM_HEAD_MORE_INFO_REQUESTED":
      return {
        icon: IconClipboardList,
        iconColor: "var(--mantine-color-violet-6)",
        circleBg: "var(--mantine-color-violet-0)",
      };
    default:
      return {
        icon: IconUsers,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
  }
}

function formatRelativeTime(iso: string) {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function UnreadDot() {
  return (
    <Box
      w={6}
      h={6}
      style={{
        borderRadius: "50%",
        backgroundColor: UNREAD_DOT,
        flexShrink: 0,
        alignSelf: "center",
      }}
    />
  );
}

type NotificationBellProps = {
  /** Icon-only trigger for collapsed sidebar rails */
  compact?: boolean;
  /** Styling for dark facilitator sidebar backgrounds */
  dark?: boolean;
};

export default function NotificationBell({ compact = false, dark = false }: NotificationBellProps) {
  const navigate = useNavigate();
  const { items, unreadCount, loading, refresh, markRead, markAllRead } = useNotifications();

  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const compactTooltip =
    unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications";

  const handleOpen = () => {
    void refresh();
  };

  const handleItemClick = async (id: string, actionUrl: string | null, readAt: string | null) => {
    if (!readAt) {
      await markRead(id);
    }
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  return (
    <Menu
      position="bottom-end"
      width={MENU_WIDTH}
      shadow="md"
      onOpen={handleOpen}
      offset={{ mainAxis: 8, crossAxis: -(BELL_TARGET_WIDTH + 6) }}
    >
      <Menu.Target>
        {compact ? (
          <Tooltip label={compactTooltip} position="right" withArrow>
            <UnstyledButton
              aria-label="Notifications"
              w="100%"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 44,
                marginLeft: -8,
                marginRight: -8,
                paddingLeft: 8,
                paddingRight: 8,
                borderRadius: 10,
              }}
            >
              <Indicator
                inline
                label={badgeLabel}
                size={16}
                disabled={unreadCount === 0}
                color="red"
                offset={4}
              >
                <IconBell size={22} stroke={1.8} color="rgba(255, 255, 255, 0.95)" />
              </Indicator>
            </UnstyledButton>
          </Tooltip>
        ) : (
          <Indicator
            inline
            label={badgeLabel}
            size={16}
            disabled={unreadCount === 0}
            color="red"
            offset={4}
          >
            <ActionIcon
              size="lg"
              variant={dark ? "subtle" : "light"}
              color={dark ? "gray.0" : undefined}
              radius="xl"
              aria-label="Notifications"
              style={
                dark
                  ? {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      color: "rgba(255, 255, 255, 0.95)",
                    }
                  : undefined
              }
            >
              <IconBell size={18} />
            </ActionIcon>
          </Indicator>
        )}
      </Menu.Target>
      <Menu.Dropdown
        p={0}
        style={{
          overflow: "hidden",
          border: `1px solid ${NAVY_BORDER}`,
        }}
      >
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          px="md"
          py="sm"
          style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}
        >
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
              size={36}
              radius="xl"
              variant="light"
              color="blue"
              style={{ backgroundColor: "var(--mantine-color-blue-0)" }}
            >
              <IconBell size={18} stroke={1.75} color="var(--mantine-color-blue-6)" />
            </ThemeIcon>
            <Text fw={700} size="sm" c="dark.7">
              Notifications
            </Text>
          </Group>
          {unreadCount > 0 && (
            <UnstyledButton
              onClick={() => void markAllRead()}
              style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
            >
              <IconCircleCheck size={16} color="var(--mantine-color-blue-6)" stroke={1.75} />
              <Text size="sm" fw={500} c="blue.6">
                Mark all read
              </Text>
            </UnstyledButton>
          )}
        </Group>

        <Box
          className={classes.listScroll}
          style={{ maxHeight: NOTIFICATION_LIST_SCROLL_HEIGHT }}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {loading && items.length === 0 ? (
            <Box py="xl" style={{ display: "flex", justifyContent: "center" }}>
              <Loader size="sm" />
            </Box>
          ) : items.length === 0 ? (
            <Text size="sm" c="dimmed" px="md" py="lg" ta="center">
              You don&apos;t have any notifications
            </Text>
          ) : (
            items.map((n) => {
              const isUnread = !n.readAt;
              const { icon: TypeIcon, iconColor, circleBg } = getNotificationIconConfig(n.type);
              return (
                <UnstyledButton
                  key={n.id}
                  w="100%"
                  onClick={() => void handleItemClick(n.id, n.actionUrl, n.readAt)}
                  style={{
                    display: "block",
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                    backgroundColor: isUnread ? UNREAD_BG : READ_BG,
                  }}
                >
                  <Group
                    gap="sm"
                    wrap="nowrap"
                    align="flex-start"
                    py="md"
                    pl="md"
                    pr={isUnread ? "sm" : "md"}
                  >
                    <ThemeIcon
                      size={40}
                      radius="xl"
                      style={{ backgroundColor: circleBg, flexShrink: 0 }}
                    >
                      <TypeIcon size={20} stroke={1.75} color={iconColor} />
                    </ThemeIcon>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                        <Text size="sm" fw={700} c="dark.7" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                          {n.title}
                        </Text>
                        <Text size="xs" c="dimmed" style={{ flexShrink: 0, lineHeight: 1.4 }}>
                          {formatRelativeTime(n.createdAt)}
                        </Text>
                      </Group>
                      <Text size="sm" c="dimmed" mt={4} lh={1.45}>
                        {n.body}
                      </Text>
                    </Box>
                    {isUnread ? <UnreadDot /> : null}
                  </Group>
                </UnstyledButton>
              );
            })
          )}
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}
